
import Vue from 'vue';
import _ from 'lodash';
import { FTProcessRow } from '@/models/FTProcessRow';
import { GoodsObject } from '@/models/GoodsObject';
import { FTProcess } from '@/models/FTProcess';
import FtProcessCell from './CFtProcessCell.vue'
import FtGoodsAutocomplete from './CFtGoodsAutocomplete.vue'
import { standardFunctions } from '@/helpers/standardFunctions';
import { ConversionResults } from './CFtProcesses';
import { compareStringIgnoreCase } from '@/utils';
import { numFormat } from '@/helpers/numFormat';
import { unit } from '@/helpers/unit';

export default Vue.extend({
  components: {
    FtProcessCell, FtGoodsAutocomplete
  },
  props: {
    processes: { type: Array, default: () => [] },
    process: FTProcess,
    orderDetails: { type: Object, default: () => {} },
    errorResponse: { type: Object, default: () => {} },
    rules: { type: Array, required: false, default: () => []  },
    disabled: { type: Boolean, default: () => false }
  },
  computed: {
    convertedGoods: function(): ConversionResults {
      const convertedValues = this.process.convert();
      if(convertedValues.outputs) {
        _.mapValues(convertedValues.outputs, (value: FTProcessRow) => {
          this.process.ftFactoryTicket.upsertCreatedGoodsObject(value.goodsObject);
        });
      }
      return convertedValues;
    },
    conversionTableData: function(): any[] {
      const nominalSpeed = `${this.process.machineDetail.mechanicalSpeed} ${this.process.machineDetail.isContinuous ? `meter/min` : 'stroke/min'}`;
      let conversionTableRow: any = { ...this.convertedGoods.details, nominalSpeed };
      let sampleOutputGoodsObject = this.convertedGoods.outputs[0].goodsObject;
      const convertToSQMFlag = compareStringIgnoreCase(_.get(sampleOutputGoodsObject, 'rawMaterialUnit', ''), 'SQM');
      conversionTableRow.wastePercentage = this.getRoundedValueWithUnit(conversionTableRow.wastePercentage, 2, '%');
      conversionTableRow.waste = this.getRoundedValueWithUnit(conversionTableRow.waste, 3, 'kg', convertToSQMFlag, sampleOutputGoodsObject);
      conversionTableRow.netWeight = this.getRoundedValueWithUnit(conversionTableRow.netWeight, 3, 'kg', convertToSQMFlag, sampleOutputGoodsObject);
      conversionTableRow.plannedHours = this.getRoundedValueWithUnit(conversionTableRow.plannedHours, 3, 'hour');
      conversionTableRow.linearMeters = this.getRoundedValueWithUnit(conversionTableRow.linearMeters, 3, 'meter');
      conversionTableRow.numberOfPieces = this.getRoundedValueWithUnit(conversionTableRow.numberOfPieces, 3, '');
      return [ conversionTableRow ];
    }
  },
  methods: {
    getRoundedValueWithUnit(inputValue?: number, decimalPositions?: number, unitName?: string, convertToSQMFlag?: boolean, outputGoodsObject?: GoodsObject): string {
      if(inputValue) {
        let valueToBeRounded = inputValue;
        let updatedUnit = unitName;
        if(convertToSQMFlag) {
          valueToBeRounded = standardFunctions['kgToSQM'](valueToBeRounded, outputGoodsObject);
          updatedUnit = _.get(outputGoodsObject, 'rawMaterialUnit', '');
        }
        const roundedValue = _.round(valueToBeRounded, decimalPositions);
        return unit(roundedValue, `${updatedUnit}`);
      }
      return `${inputValue}`;
    },
    inputCheckErr(value: number, index: string) {
      const hasServerError = !!this.errorResponse && this.errorResponse[index] && this.errorResponse[index].rm === value;
      if(hasServerError) {
        return `Quantiy Missing: ${this.errorResponse[index].quantityMissing * -1}`;
      } else {
        return true;
      }
    },
    customFilter: (item: any, queryText: string) => {
      return item.__text.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
    },
    checkVisibilityStatus: function(showIf?: boolean): boolean {
      if(showIf !== undefined) {
        return showIf;
      } else {
        return true;
      }
    },
    checkConvertFnStatus: function(enableConvertFnIf?: boolean): boolean {
      if(enableConvertFnIf !== undefined) {
        return enableConvertFnIf;
      } else {
        return false;
      }
    },
    getTableKeysForPrintProcessRows(type: string, ftProcessRows: FTProcessRow[]): any[] {
      let headersObject: any = { text: { value: 'text', text: type == 'input' ? 'Input Goods' : 'Output Goods' } };
      _.forEach(ftProcessRows, (currentProcessRow: FTProcessRow) => {
        const params = type == 'input' ? currentProcessRow.processRowConfig.params : currentProcessRow.processRowConfig.arguments;
        const fiteredParamsForPrint = _.filter(params, (param: any) => param.disablePrint != true );
        _.forEach(fiteredParamsForPrint, (argument: any) => {
          if(this.checkVisibilityStatus(argument.showIf) && !Object.keys(headersObject).includes(argument.name)) {
            headersObject[argument.name]= { value: argument.name, text: argument.name, type: argument.type };
          }
        })
      });
      return Object.values(headersObject);
    },
    getProcessRowDetailsForPrint(type: string, ftProcessRows: FTProcessRow[]): any[] {
      const tableRows = _.map(ftProcessRows, (currentProcessRow: FTProcessRow) => {
        let resolvedValues: any = { text: _.get(currentProcessRow, 'goodsObject.__text', '') };
        const params = type == 'input' ? currentProcessRow.processRowConfig.params : currentProcessRow.processRowConfig.arguments;
        const fiteredParamsForPrint = _.filter(params, (param: any) => param.disablePrint != true );
        _.forEach(fiteredParamsForPrint, (argument: any) => {
          if(this.checkVisibilityStatus(argument.showIf)) {
            if(argument.type == 'quantity') {
              let quantity = _.get(currentProcessRow, argument.value)
              if(this.checkConvertFnStatus(argument.enableConvertFnIf)) {
                quantity = standardFunctions['kgToSQM'](quantity, currentProcessRow.goodsObject);
              }
              resolvedValues[argument.name] = `${numFormat(quantity)} ${_.get(currentProcessRow, 'goodsObject.rawMaterialUnit')}`;
            } else if(argument.type == 'label') {
              resolvedValues[argument.name] = numFormat(argument.value, true) + ` ${_.get(argument, 'unit', '')}`;
            } else if(argument.type === 'shape' || argument.type === 'dimension') {
              resolvedValues[argument.name] = _.get(currentProcessRow,`dimensions.${argument.value}`) + ` ${_.get(argument, 'unit', '')}`;
            } else if(argument.type === 'dropdown' || argument.type === 'text') {
              resolvedValues[argument.name] = `${_.get(currentProcessRow,  argument.value)} ${_.get(argument, 'unit', '')}`;
            } else if (argument.type === 'checkbox') {
              resolvedValues[argument.name] = `${_.get(currentProcessRow,  argument.value)}`;
            }
          }
        })
        return resolvedValues;
      });
      return tableRows;
    }
  }
})
